/**
 * Cards.
 *
 * Four-colour deck: spades black, hearts red, diamonds blue, clubs green. It
 * looks unusual for about one hand and then it is strictly better — the
 * mistake it prevents is misreading a flush at a glance, which is the single
 * most expensive misread in the game.
 *
 * Community cards turn over rather than fade in. A card appearing out of
 * nothing reads as a rendering glitch; a card flipping reads as a card being
 * dealt, and it gives the eye the beat it needs to notice that the board
 * changed at all. The flop staggers so three cards land as three events, not
 * one blur.
 */

const SUIT_GLYPH: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };
const SUIT_CLASS: Record<string, string> = { s: 'spade', h: 'heart', d: 'diamond', c: 'club' };

export type CardSize = 'board' | 'hole' | 'mini';

function Face({ code }: { code: string }) {
  const rank = code[0] ?? '?';
  const suit = code[1] ?? 's';
  return (
    <span className={`face front ${SUIT_CLASS[suit] ?? 'spade'}`}>
      <span className="index">
        <span className="rank">{rank === 'T' ? '10' : rank}</span>
        <span className="pip-small">{SUIT_GLYPH[suit]}</span>
      </span>
      {/* One large suit, filling the rest of the card.
          A real deck also prints the index upside down in the far corner, and
          for a moment this one did — but a real card is 63mm across and this
          one is 58 pixels. At that size the second index and the large suit
          are fighting over the same corner, and the card stops reading as a
          card. What survives the cut is the thing you actually read: the rank
          in the corner, and one unmistakable suit. */}
      <span className="pip" aria-hidden="true">{SUIT_GLYPH[suit]}</span>
    </span>
  );
}

export function PlayingCard({
  code,
  size = 'hole',
  dimmed = false,
  /** Turn the card over on arrival. Board cards do; a hand you already hold does not. */
  flip = false,
  /** Position within its group, for the stagger. */
  order = 0,
  /**
   * Place in the deal, for a card coming out at the start of a hand.
   *
   * Undefined for a card that is simply already there — a re-render, a
   * reconnect, a hand you have been looking at for a while. The deal is
   * something that happens once.
   */
  deal,
  /**
   * Place in the showdown, for a card being turned over.
   *
   * A hand you are shown is not a hand you are dealt. One arrives from the
   * middle of the felt; the other was face down in front of somebody the whole
   * time and simply turns over where it lies.
   */
  reveal,
}: {
  code: string;
  size?: CardSize;
  dimmed?: boolean;
  flip?: boolean;
  order?: number;
  deal?: number | undefined;
  reveal?: number | undefined;
}) {
  return (
    <div className={`card-slot ${size}${dimmed ? ' dimmed' : ''}`} aria-label={code}>
      <div
        className={`card-flip${flip ? ' dealing' : ''}${deal !== undefined ? ' dealt' : ''}${
          reveal !== undefined ? ' turned' : ''
        }`}
        // Wider stagger, so a flop lands as three cards rather than a blur.
        style={
          flip
            ? { animationDelay: `${order * 260}ms` }
            : deal !== undefined
              ? { animationDelay: `${deal * 120}ms` }
              : reveal !== undefined
                ? { animationDelay: `${reveal * 320}ms` }
                : undefined
        }
      >
        <span className="face back" />
        <Face code={code} />
      </div>
    </div>
  );
}

export function CardBack({
  size = 'hole',
  deal,
}: {
  size?: CardSize;
  /** Place in the deal. Somebody else's cards come out in the same order yours do. */
  deal?: number | undefined;
}) {
  return (
    <div className={`card-slot ${size}`} aria-label="کارت بسته">
      <div
        className={`card-flip${deal !== undefined ? ' dealt' : ''}`}
        style={deal === undefined ? undefined : { animationDelay: `${deal * 120}ms` }}
      >
        <span className="face back solo" />
      </div>
    </div>
  );
}
